import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Typography,
  IconButton,
  Box,
  Divider,
} from "@mui/material";
import { useContext, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { authActions } from "../../store/auth-slice";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  login,
  signup,
  forgottenPassword,
  resetPassword,
  loginWithFb,
  loginWithApple,
} from "../../api";
import UserContext from "../../contexts/UserContext";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";
import FacebookIcon from "@mui/icons-material/Facebook";
import AppleIcon from "@mui/icons-material/Apple";

const AuthForm = (props) => {
  const vibyAccentColor = "#d3128b";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authCtx = useSelector((store) => store.auth);
  const [searchParams] = useSearchParams();

  const handleCancel = () => {
    dispatch(authActions.closeForm());
    navigate("/events");
  };

  const userContext = useContext(UserContext);

  const blankAuthFields = {
    usernameOrEmail: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  };

  const [state, setState] = useState({});
  const [currentTitle, setCurrentTitle] = useState(props.title);
  const [authFields, setAuthFields] = useState(blankAuthFields);
  const isAuthTitle = currentTitle === "Log in" || currentTitle === "Sign up";

  const handleFieldChange = (field) => (e) => {
    const { value } = e.target;
    setAuthFields((f) => ({ ...f, [field]: value }));
  };

  useEffect(() => {
    setCurrentTitle(props.title);
  }, [props.title]);

  // Wipe every field whenever the auth mode changes so nothing typed in one
  // mode (or refilled by browser autofill) carries over to the other.
  useEffect(() => {
    setAuthFields(blankAuthFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTitle]);

  const handleConfirm = async () => {
    setState((s) => ({
      ...s,
      errorMessage: undefined,
      isLoading: true,
    }));
    if (currentTitle === "Log in") {
      const user = {
        usernameOrEmail: authFields.usernameOrEmail,
        password: authFields.password,
      };
      try {
        const result = await login(user);
        userContext.setUser({ ...result });
        handleCancel();
      } catch (e) {
        setState((s) => ({
          ...s,
          errorMessage: e?.message || "Unable to log in. Please try again.",
          isLoading: false,
        }));
      }
    }
    if (currentTitle === "Sign up") {
      const user = {
        username: authFields.username,
        email: authFields.email,
        password: authFields.password,
        confirmPassword: authFields.confirmPassword,
        role: "dj",
      };
      try {
        const result = await signup(user);
        userContext.setUser({ ...result });
        handleCancel();
      } catch (e) {
        setState((s) => ({
          ...s,
          errorMessage: e,
          isLoading: false,
        }));
      }
    }
    if (currentTitle === "Forgotten Password") {
      const user = {
        email: authFields.email,
      };
      try {
        await forgottenPassword(user);
        setState((s) => ({
          ...s,
          successMessage: `A password reset link has been sent to ${authFields.email}.`,
          isLoading: false,
        }));
      } catch (e) {
        setState((s) => ({
          ...s,
          errorMessage: e,
          isLoading: false,
        }));
      }
    }
    if (currentTitle === "Reset Password") {
      const user = {
        email: searchParams.get("email"),
        token: searchParams.get("token"),
        password: authFields.password,
        confirmPassword: authFields.confirmPassword,
      };
      try {
        const result = await resetPassword(user);
        userContext.setUser(result);
        handleCancel();
      } catch (e) {
        setState((s) => ({
          ...s,
          errorMessage: e,
          isLoading: false,
        }));
      }
    }
  };

  const handleSwitchAuthMode = () => {
    const nextTitle = currentTitle === "Log in" ? "Sign up" : "Log in";
    setState({});
    setAuthFields(blankAuthFields);
    setCurrentTitle(nextTitle);
    navigate(nextTitle === "Log in" ? "/login" : "/register", { replace: true });
  };

  const handleLoginWithFb = async (e) => {
    setState((s) => ({
      ...s,
      errorMessage: undefined,
      isLoading: true,
    }));
    try {
      const result = await loginWithFb({
        token: e.accessToken,
        userId: e.userID,
        email: e.email,
        name: e.name,
        role: "dj",
      });
      userContext.setUser(result);
      handleCancel();
    } catch (e) {
      setState((s) => ({
        ...s,
        errorMessage: "An error occurred.",
        isLoading: false,
      }));
    }
  };

  const handleLoginWithApple = async () => {
    setState((s) => ({
      ...s,
      errorMessage: undefined,
      isLoading: true,
    }));
    try {
      const response = await window.AppleID.auth.signIn();
      const result = await loginWithApple({
        identityToken: response?.authorization?.id_token,
        // email: response?.user?.email,
        firstName: response?.user?.name?.firstName,
        lastName: response?.user?.name?.lastName,
        clientId: "com.test.vibysignin",
        role: "dj",
      });
      userContext.setUser(result);
      handleCancel();
    } catch (e) {
      if (e.error !== "popup_closed_by_user") {
        setState((s) => ({
          ...s,
          errorMessage: "An error occurred.",
          isLoading: false,
        }));
      } else {
        setState((s) => ({
          ...s,
          isLoading: false,
        }));
      }
    }
  };

  useEffect(() => {
    const script = document.createElement("script");

    if (currentTitle === "Log in") {
      script.textContent = `
      AppleID.auth.init({
        clientId: "com.test.vibysignin",
        scope: "name email",
        redirectURI: "https://app.viby-music.com/login",
        usePopup: true,
      });
      `;
    } else if (currentTitle === "Sign up") {
      script.textContent = `
      AppleID.auth.init({
        clientId: "com.test.vibysignin",
        scope: "name email",
        redirectURI: "https://app.viby-music.com/register",
        usePopup: true,
      });
      `;
    }

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [currentTitle]);

  const authInputSx = {
    marginTop: "clamp(2px, 1vh, 10px)",
    marginBottom: "clamp(2px, 0.5vh, 4px)",
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "rgba(255, 255, 255, 0.04)",
      color: "#f3edff",
      "& .MuiInputBase-input": {
        fontSize: "clamp(0.78rem, 1.7vh, 0.9rem)",
        padding: "clamp(6px, 1.3vh, 8.5px) 14px",
        color: "#f3edff !important",
        WebkitTextFillColor: "#f3edff !important",
        caretColor: "#f3edff",
      },
      "& .MuiInputBase-input:focus": {
        color: "#f3edff !important",
        WebkitTextFillColor: "#f3edff !important",
      },
      "& .MuiInputBase-input:-webkit-autofill, & .MuiInputBase-input:-webkit-autofill:hover, & .MuiInputBase-input:-webkit-autofill:focus, & .MuiInputBase-input:-webkit-autofill:active": {
        WebkitTextFillColor: "#f3edff !important",
        caretColor: "#f3edff !important",
        WebkitBoxShadow: "0 0 0 1000px #261844 inset !important",
        boxShadow: "0 0 0 1000px #261844 inset !important",
        borderRadius: "14px",
        transition: "background-color 99999s ease-in-out 0s",
      },
      "& .MuiInputBase-input:-moz-autofill": {
        boxShadow: "0 0 0 1000px #261844 inset !important",
        textFillColor: "#f3edff !important",
        color: "#f3edff !important",
      },
      "& fieldset": {
        borderColor: "transparent",
      },
      "& fieldset legend": {
        fontSize: "clamp(0.585rem, 1.28vh, 0.75rem)",
      },
      "&:hover fieldset": {
        borderColor: "transparent",
      },
      "&.Mui-focused fieldset": {
        borderColor: `${vibyAccentColor} !important`,
        borderWidth: "1px",
      },
    },
    "& .MuiInputLabel-root": {
      color: "#cdbbe8",
      fontSize: "clamp(0.78rem, 1.7vh, 1rem)",
      // Keeps the resting label vertically centred as the input padding shrinks.
      transform: "translate(14px, clamp(6px, 1.3vh, 9px)) scale(1)",
      "&.MuiInputLabel-shrink": {
        transform: "translate(14px, -9px) scale(0.75)",
      },
      "&.Mui-focused": {
        color: vibyAccentColor,
      },
    },
  };

  // Height of a single dense field row, mirroring the responsive metrics above.
  const authFieldRow =
    "clamp(2px, 1vh, 10px) + clamp(2px, 0.5vh, 4px) + 2 * clamp(6px, 1.3vh, 8.5px) + 1.4375 * clamp(0.78rem, 1.7vh, 0.9rem)";
  // Log in renders two inputs where Sign up renders four, so the field area always
  // reserves room for four rows to stop the dialog resizing when modes switch.
  const authFieldsMinHeight = `calc(4 * (${authFieldRow}))`;

  return (
    <>
      <Dialog
        open={authCtx.shouldOpenAuthForm}
        onBackdropClick={isAuthTitle ? undefined : handleCancel}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(8, 4, 20, 0.58)",
            backdropFilter: "blur(2px)",
          },
        }}
        PaperProps={{
          sx: {
            backgroundColor: "#1b0f30",
            color: "#e4d8f8",
            borderRadius: "22px",
            width: "100%",
            maxWidth: "470px",
            margin: "clamp(8px, 2vh, 32px)",
            maxHeight: "calc(100% - 2 * clamp(8px, 2vh, 32px))",
            border: "1px solid rgba(147, 125, 184, 0.35)",
            boxShadow: "0 25px 80px rgba(0, 0, 0, 0.55)",
            overflow: "hidden",
          },
        }}
      >
        {isAuthTitle ? (
          <>
            <DialogContent
              sx={{
                padding:
                  "clamp(14px, 3.2vh, 28px) clamp(16px, 5vw, 28px) clamp(6px, 1.6vh, 16px)",
                overflowY: "auto",
                scrollbarWidth: "thin",
                scrollbarColor: "#d3128b rgba(255, 255, 255, 0.08)",
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "rgba(255, 255, 255, 0.08)",
                  borderRadius: "999px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#d3128b",
                  borderRadius: "999px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "#e71ca0",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: "clamp(2px, 1vh, 8px)",
                }}
              >
                <img
                  src="/logo.png"
                  alt="Viby logo"
                  style={{
                    maxWidth: "clamp(92px, 20vh, 132px)",
                    width: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
              <Typography
                variant="h5"
                align="center"
                sx={{
                  fontWeight: 700,
                  color: "#f4efff",
                  fontSize: "clamp(1.05rem, 2.7vh, 1.5rem)",
                  lineHeight: 1.25,
                  marginBottom: "clamp(3px, 1vh, 8px)",
                }}
              >
                {currentTitle === "Log in"
                  ? "Log in and keep the vibe going"
                  : "Create your VIBY account"}
              </Typography>
              <Typography
                align="center"
                sx={{
                  color: "#bfaeda",
                  fontSize: "clamp(0.78rem, 1.8vh, 0.95rem)",
                  lineHeight: 1.4,
                  marginBottom: "clamp(6px, 1.8vh, 16px)",
                }}
              >
                {currentTitle === "Log in"
                  ? "Enter your account details to continue"
                  : "Set up your profile in a few seconds"}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 1.5,
                  mb: "clamp(6px, 1.6vh, 12px)",
                }}
              >
                <FacebookLogin
                  appId="1871655919785755"
                  fields="name,email"
                  callback={handleLoginWithFb}
                  isMobile={false}
                  render={(renderProps) => (
                    <IconButton
                      onClick={renderProps.onClick}
                      title={
                        currentTitle === "Sign up"
                          ? "Sign up with Facebook"
                          : "Log in with Facebook"
                      }
                      sx={{
                        width: "clamp(76px, 16vh, 96px)",
                        height: "clamp(34px, 7vh, 45px)",
                        borderRadius: "999px",
                        border: "1px solid rgba(255, 255, 255, 0.25)",
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                        color: "#53a4ff",
                      }}
                    >
                      <FacebookIcon
                        sx={{ fontSize: "clamp(22px, 5vh, 32px)" }}
                      />
                    </IconButton>
                  )}
                />
                <IconButton
                  onClick={handleLoginWithApple}
                  title={
                    currentTitle === "Sign up"
                      ? "Sign up with Apple"
                      : "Log in with Apple"
                  }
                  sx={{
                    width: "clamp(76px, 16vh, 96px)",
                    height: "clamp(34px, 7vh, 45px)",
                    borderRadius: "999px",
                    border: "1px solid rgba(255, 255, 255, 0.25)",
                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                    color: "#f5f5f5",
                  }}
                >
                  <AppleIcon sx={{ fontSize: "clamp(22px, 5vh, 32px)" }} />
                </IconButton>
              </Box>

              <Typography
                align="center"
                sx={{
                  fontSize: "clamp(0.7rem, 1.5vh, 0.8rem)",
                  color: "#a899c2",
                  marginBottom: "clamp(3px, 1vh, 8px)",
                }}
              >
                or use your credentials
              </Typography>

              <Divider sx={{ borderColor: "rgba(170, 148, 206, 0.22)" }} />

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  minHeight: authFieldsMinHeight,
                  justifyContent: "center",
                }}
              >
                {currentTitle === "Log in" && (
                  <TextField
                    autoFocus
                    autoComplete="off"
                    margin="dense"
                    size="small"
                    id="usernameOrEmail"
                    label="Username or Email"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={authFields.usernameOrEmail}
                    onChange={handleFieldChange("usernameOrEmail")}
                    sx={authInputSx}
                  />
                )}
                {currentTitle === "Sign up" && (
                  <TextField
                    autoFocus
                    autoComplete="off"
                    margin="dense"
                    size="small"
                    id="username"
                    label="Username"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={authFields.username}
                    onChange={handleFieldChange("username")}
                    sx={authInputSx}
                  />
                )}
                {currentTitle === "Sign up" && (
                  <TextField
                    autoComplete="off"
                    margin="dense"
                    size="small"
                    id="email"
                    label="Email Address"
                    type="email"
                    fullWidth
                    variant="outlined"
                    value={authFields.email}
                    onChange={handleFieldChange("email")}
                    sx={authInputSx}
                  />
                )}
                <TextField
                  autoComplete="off"
                  margin="dense"
                  size="small"
                  id="password"
                  label="Password"
                  type="password"
                  fullWidth
                  variant="outlined"
                  value={authFields.password}
                  onChange={handleFieldChange("password")}
                  sx={authInputSx}
                />
                {currentTitle === "Sign up" && (
                  <TextField
                    autoComplete="off"
                    margin="dense"
                    size="small"
                    id="confirmPassword"
                    label="Confirm Password"
                    type="password"
                    fullWidth
                    variant="outlined"
                    value={authFields.confirmPassword}
                    onChange={handleFieldChange("confirmPassword")}
                    sx={authInputSx}
                  />
                )}

                {state.errorMessage && (
                  <Alert severity="error" sx={{ mt: 1.5 }}>
                    {state.errorMessage}
                  </Alert>
                )}

                {currentTitle === "Log in" && !state.errorMessage && (
                  <Box sx={{ mt: 1, textAlign: "right" }}>
                    <a
                      href="/forgottenpassword"
                      style={{
                        color: "#fff",
                        fontSize: "clamp(0.74rem, 1.6vh, 0.875rem)",
                      }}
                    >
                      Forgotten Password
                    </a>
                  </Box>
                )}
              </Box>

              <Typography
                sx={{
                  marginTop: "clamp(6px, 1.6vh, 14px)",
                  textAlign: "center",
                  fontSize: "clamp(0.7rem, 1.5vh, 0.82rem)",
                  lineHeight: 1.4,
                  color: "#b8a9d3",
                  "& a": {
                    color: "inherit",
                  },
                }}
              >
                By clicking {currentTitle}, you acknowledge the{" "}
                <a href="/termsofuse">Terms of Use</a> and{" "}
                <a href="/privacypolicy">Privacy Policy</a>
              </Typography>

              <Box
                sx={{
                  marginTop: "clamp(2px, 1.2vh, 12px)",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#d9cff0",
                    fontSize: "clamp(0.74rem, 1.6vh, 0.875rem)",
                  }}
                >
                  {currentTitle === "Log in" ? (
                    <>
                      Don&apos;t have account yet:{" "}
                      <Button
                        variant="text"
                        onClick={handleSwitchAuthMode}
                        sx={{
                          minWidth: 0,
                          padding: 0,
                          textTransform: "none",
                          fontSize: "inherit",
                          color: "#fff",
                        }}
                      >
                        Sign up
                      </Button>
                    </>
                  ) : (
                    <>
                      Already have account:{" "}
                      <Button
                        variant="text"
                        onClick={handleSwitchAuthMode}
                        sx={{
                          minWidth: 0,
                          padding: 0,
                          textTransform: "none",
                          fontSize: "inherit",
                          color: "#fff",
                        }}
                      >
                        Log in
                      </Button>
                    </>
                  )}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions
              sx={{
                padding:
                  "0 clamp(16px, 5vw, 28px) clamp(10px, 3vh, 26px)",
              }}
            >
              <Button
                type="submit"
                onClick={handleConfirm}
                variant="contained"
                fullWidth
                disabled={state.isLoading}
                sx={{
                  borderRadius: "999px",
                  height: "clamp(36px, 7.5vh, 46px)",
                  fontSize: "clamp(0.8rem, 1.8vh, 0.875rem)",
                  fontWeight: 700,
                  textTransform: "none",
                  backgroundColor: vibyAccentColor,
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#e71ca0",
                  },
                }}
              >
                {currentTitle}
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>{currentTitle}</DialogTitle>
            <DialogContent>
          {currentTitle === "Log in" && (
            <TextField
              autoFocus
              autoComplete="off"
              margin="dense"
              id="usernameOrEmail"
              label="Username/Email"
              type="text"
              fullWidth
              variant="outlined"
              value={authFields.usernameOrEmail}
              onChange={handleFieldChange("usernameOrEmail")}
              sx={authInputSx}
            />
          )}
          {currentTitle === "Sign up" && (
            <TextField
              autoFocus
              autoComplete="off"
              margin="dense"
              id="username"
              label="Username"
              type="text"
              fullWidth
              variant="outlined"
              value={authFields.username}
              onChange={handleFieldChange("username")}
              sx={authInputSx}
            />
          )}
          {(currentTitle === "Sign up" ||
            currentTitle === "Forgotten Password") && (
            <TextField
              autoComplete="off"
              margin="dense"
              id="email"
              label="Email Address"
              type="email"
              fullWidth
              variant="outlined"
              value={authFields.email}
              onChange={handleFieldChange("email")}
              sx={authInputSx}
            />
          )}
          {(currentTitle === "Sign up" ||
            currentTitle === "Log in" ||
            currentTitle === "Reset Password") && (
            <TextField
              autoComplete="off"
              margin="dense"
              id="password"
              label="Password"
              type="password"
              fullWidth
              variant="outlined"
              value={authFields.password}
              onChange={handleFieldChange("password")}
              sx={authInputSx}
            />
          )}
          {(currentTitle === "Sign up" || currentTitle === "Reset Password") && (
            <TextField
              autoComplete="off"
              margin="dense"
              id="confirmPassword"
              label="Confirm Password"
              type="password"
              fullWidth
              variant="outlined"
              value={authFields.confirmPassword}
              onChange={handleFieldChange("confirmPassword")}
              sx={authInputSx}
            />
          )}
          {state.errorMessage && (
            <Alert severity="error">
              {state.errorMessage}
              {currentTitle === "Sign up" || currentTitle === "Log in" ? (
                <>
                  {(currentTitle === "Sign up" || currentTitle === "Log in") && (
                    <>
                      {" "}
                      <a href="/forgottenpassword">Forgot your password?</a>
                    </>
                  )}
                </>
              ) : (
                <></>
              )}
            </Alert>
          )}
          {(currentTitle === "Sign up" || currentTitle === "Log in") &&
            !state.errorMessage && (
              <a href="/forgottenpassword">Forgotten Password</a>
            )}
          {currentTitle === "Forgotten Password" && state.successMessage && (
            <Alert severity="success">{state.successMessage}</Alert>
          )}
          <div style={{ marginTop: 16 }}>
            {(currentTitle === "Sign up" || currentTitle === "Log in") &&
              !state.errorMessage && (
                <>
                  By clicking {currentTitle}, you acknowledge that you have
                  agreed to the <a href="/termsofuse">Terms of Use</a>, and read
                  the <a href="/privacypolicy">Privacy Policy</a>
                </>
              )}
          </div>
            </DialogContent>
            <DialogActions>
              {!state.successMessage ? (
              <>
                <Button onClick={handleCancel} variant="contained">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  onClick={handleConfirm}
                  variant="contained"
                  disabled={state.isLoading}
                >
                  Confirm
                </Button>
              </>
              ) : (
                <Button onClick={handleCancel} variant="contained">
                  OK
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default AuthForm;
